/**
 * Smooth FAQ accordion — height + opacity slide like jQuery slideToggle.
 */
class YanaFaq extends HTMLElement {
  static #duration = 360;
  static #easing = 'cubic-bezier(0.25, 0.1, 0.25, 1)';

  connectedCallback() {
    this.#items = [...this.querySelectorAll('details.yana-faq__item')];
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#items.forEach((details) => {
      const summary = details.querySelector('summary');
      const panel = details.querySelector('.yana-faq__panel');
      if (!(summary instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

      summary.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          if (details.dataset.animating === 'true') return;
          if (details.open) this.#close(details, panel);
          else this.#open(details, panel);
        },
        { signal }
      );
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @type {HTMLDetailsElement[]} */
  #items = [];

  /** @type {AbortController | undefined} */
  #controller;

  #reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} panel
   */
  async #open(details, panel) {
    details.dataset.animating = 'true';
    details.open = true;

    if (this.#reduceMotion()) {
      details.dataset.animating = 'false';
      return;
    }

    const endHeight = panel.scrollHeight;
    panel.style.overflow = 'hidden';
    panel.style.height = '0px';
    panel.style.opacity = '0';
    panel.offsetHeight;

    panel.style.transition = `height ${YanaFaq.#duration}ms ${YanaFaq.#easing}, opacity ${YanaFaq.#duration}ms ${YanaFaq.#easing}`;
    panel.style.height = `${endHeight}px`;
    panel.style.opacity = '1';

    await this.#wait(panel);
    this.#clearInline(panel);
    details.dataset.animating = 'false';
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} panel
   */
  async #close(details, panel) {
    details.dataset.animating = 'true';

    if (this.#reduceMotion()) {
      details.open = false;
      details.dataset.animating = 'false';
      return;
    }

    const startHeight = panel.scrollHeight;
    panel.style.overflow = 'hidden';
    panel.style.height = `${startHeight}px`;
    panel.style.opacity = '1';
    panel.offsetHeight;

    panel.style.transition = `height ${YanaFaq.#duration}ms ${YanaFaq.#easing}, opacity ${YanaFaq.#duration}ms ${YanaFaq.#easing}`;
    panel.style.height = '0px';
    panel.style.opacity = '0';

    await this.#wait(panel);
    details.open = false;
    this.#clearInline(panel);
    details.dataset.animating = 'false';
  }

  /**
   * @param {HTMLElement} panel
   */
  #clearInline(panel) {
    panel.style.height = '';
    panel.style.overflow = '';
    panel.style.transition = '';
    panel.style.opacity = '';
  }

  /**
   * @param {HTMLElement} el
   */
  #wait(el) {
    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        el.removeEventListener('transitionend', onEnd);
        resolve();
      };
      /** @param {TransitionEvent} event */
      const onEnd = (event) => {
        if (event.target === el && event.propertyName === 'height') finish();
      };
      el.addEventListener('transitionend', onEnd);
      window.setTimeout(finish, YanaFaq.#duration + 80);
    });
  }
}

if (!customElements.get('yana-faq')) {
  customElements.define('yana-faq', YanaFaq);
}
